import { stableRecordKey } from '../lib/rowIdentity';
import React, { useState } from 'react';
import { Search, Loader2, MapPin, PencilLine } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * UK address lookup: type a postcode -> "Find address" -> pick from the list -> address + postcode are filled in.
 * Falls back to manual entry at any time. Emits { address, postcode, address_line_1, address_line_2, city, county }
 * via onChange. The structured parts are only ever set when an address is picked from the lookup list (so they
 * can be composed server-side in the same order imported members use); hand-typed edits to the Address box
 * clear the parts so the typed text is saved as a single line, exactly as entered.
 *
 * props:
 *  - address, postcode, address_line_1, address_line_2, city, county: current values (controlled)
 *  - onChange({ address, postcode, address_line_1, address_line_2, city, county })
 *  - required (default true)
 *  - testIdPrefix (e.g. "luxury-membership")
 *  - labelClassName (optional)
 */
export const AddressLookup = ({ address = '', postcode = '', address_line_1 = '', address_line_2 = '', city = '', county = '', onChange, required = true, testIdPrefix = 'address', className = '' }) => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [locality, setLocality] = useState(null);
  const [error, setError] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [manual, setManual] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState('');

  const emit = (patch) => onChange && onChange({ address, postcode, address_line_1, address_line_2, city, county, ...patch });

  const lookup = async (pcOverride) => {
    const pc = (pcOverride ?? postcode).trim();
    if (!pc) { setError('Please enter your postcode first'); return; }
    setLoading(true); setError(''); setSuggestions([]); setResults([]); setLocality(null); setSelectedIdx('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/address/lookup?postcode=${encodeURIComponent(pc)}`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const d = body.detail;
        const msg = typeof d === 'string' ? d : (d && d.message) || 'Address lookup failed';
        setError(msg);
        if (d && Array.isArray(d.suggestions)) setSuggestions(d.suggestions);
        return;
      }
      emit({ postcode: body.postcode || pc.toUpperCase() });
      setLocality(body.locality || null);
      if (body.addresses && body.addresses.length) {
        setResults(body.addresses);
      } else {
        // Provider only knows the town: pre-fill it and let the user type house + street.
        // This is not a precise pick, so structured parts are left blank (hand-typed = single line).
        const town = [body.locality?.post_town, body.locality?.county].filter(Boolean).join(', ');
        setManual(true);
        if (town && !address) emit({ address: town, postcode: body.postcode || pc.toUpperCase() });
        setError('We found your area — please type your house number and street above the town.');
      }
    } catch {
      setError('Address lookup is unavailable right now — please enter your address manually.');
      setManual(true);
    } finally {
      setLoading(false);
    }
  };

  const pick = (idxStr) => {
    setSelectedIdx(idxStr);
    const a = results[Number(idxStr)];
    if (!a) return;
    emit({
      address: a.full_address || a.label, postcode: a.postcode || postcode,
      address_line_1: a.line_1 || '', address_line_2: [a.line_2, a.line_3].filter(Boolean).join(', '),
      city: a.post_town || '', county: a.county || '',
    });
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); lookup(); }
  };

  return (
    <div className={`space-y-3 ${className}`} data-testid={`${testIdPrefix}-address-lookup`}>
      <div>
        <Label htmlFor={`${testIdPrefix}-postcode`}>Postcode {required && '*'}</Label>
        <div className="flex flex-col sm:flex-row gap-2 mt-1">
          <Input
            id={`${testIdPrefix}-postcode`}
            required={required}
            value={postcode}
            onChange={e => emit({ postcode: e.target.value.toUpperCase() })}
            onKeyDown={onKeyDown}
            placeholder="e.g. NG3 1JE"
            autoComplete="postal-code"
            className="uppercase sm:max-w-[200px]"
            data-testid={`${testIdPrefix}-postcode-input`}
          />
          <Button
            type="button"
            onClick={() => lookup()}
            disabled={loading || !postcode.trim()}
            className="bg-[hsl(var(--lux-royal))] text-white hover:bg-[hsl(var(--lux-royal-2))] h-11 sm:h-9 min-w-[150px]"
            data-testid={`${testIdPrefix}-find-address-button`}
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
            {loading ? 'Searching…' : 'Find address'}
          </Button>
        </div>
        <p className="text-xs text-[hsl(var(--lux-ink)/0.6)] mt-1">Enter your UK postcode and choose your address from the list.</p>
      </div>

      {error && (
        <div className="text-sm text-[hsl(var(--lux-vermilion,0_72%_45%))] bg-[hsl(var(--lux-ivory))] border border-[hsl(var(--lux-gold)/0.4)] rounded-md px-3 py-2" role="alert" data-testid={`${testIdPrefix}-address-lookup-error`}>
          {error}
          {suggestions.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1 items-center">
              <span className="text-xs">Did you mean:</span>
              {suggestions.map(s => (
                <button type="button" key={s} onClick={() => { emit({ postcode: s }); lookup(s); }}
                  className="text-xs px-2 py-0.5 rounded border border-[hsl(var(--lux-royal)/0.3)] text-[hsl(var(--lux-royal))] hover:bg-[hsl(var(--lux-royal-soft))]"
                  data-testid={`${testIdPrefix}-postcode-suggestion`}>
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {results.length > 0 && (
        <div data-testid={`${testIdPrefix}-address-select-wrap`}>
          <Label>Select your address ({results.length} found{locality?.post_town ? ` in ${locality.post_town}` : ''})</Label>
          <Select value={selectedIdx} onValueChange={pick}>
            <SelectTrigger className="mt-1 h-11 bg-white" data-testid={`${testIdPrefix}-address-select`}>
              <SelectValue placeholder="Choose your address…" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {results.map((a, i) => (
                <SelectItem key={stableRecordKey(a)} value={String(i)} data-testid={`${testIdPrefix}-address-option`}>
                  {a.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <Label htmlFor={`${testIdPrefix}-address`}>Address {required && '*'}</Label>
        <Input
          id={`${testIdPrefix}-address`}
          required={required}
          value={address}
          onChange={e => emit({ address: e.target.value, address_line_1: '', address_line_2: '', city: '', county: '' })}
          placeholder={manual || results.length ? 'House number, street, town' : 'Filled in automatically — or type your address here'}
          autoComplete="street-address"
          className="mt-1"
          data-testid={`${testIdPrefix}-address-input`}
        />
        <p className="text-xs text-[hsl(var(--lux-ink)/0.6)] mt-1 flex items-center gap-1">
          {address
            ? (<><MapPin className="w-3 h-3" /> You can edit this if anything is not quite right.</>)
            : (<><PencilLine className="w-3 h-3" /> Can't find your address? Just type it here.</>)}
        </p>
      </div>
    </div>
  );
};
